// src\themes\tolica\elements\Header\HeaderSelecetLanguage.tsx
'use client';

import {
    Sheet,
    SheetContent,
    SheetTitle,
    SheetTrigger,
} from '@/components/ui/sheet';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { Globe } from 'lucide-react';
import { setCookie } from 'cookies-next';
import { usePathname, useRouter } from 'next/navigation';
import { useDictionary } from '@/core/hooks/use-dictionary';
import clsx from 'clsx';

const locales = [
    { key: 'fa', label: 'fa' },
    { key: 'en', label: 'en' },
    { key: 'ar', label: 'ar' },
] as const;

type Props = {
    variant?: 'full' | 'mini';
    className?: string;
};

export default function HeaderSelectLanguage({
    variant = 'full',
    className,
}: Props) {
    const pathname = usePathname();
    const { dictionary } = useDictionary();
    const router = useRouter()

    const handleLanguageChange = (newLocale: string) => {
        setCookie('locale', newLocale, {
            maxAge: 30 * 24 * 60 * 60,
            path: '/',
        });

        const pathnameWithoutLocale = pathname.replace(
            /^\/([a-z]{2}(-[A-Z]{2})?)(?=\/|$)/,
            ''
        );

        const newPathname =
            pathnameWithoutLocale === '' || pathnameWithoutLocale === '/'
                ? `/${newLocale}`
                : `/${newLocale}${pathnameWithoutLocale}`;

        router.push(`${newPathname}${window.location.search}`);
    };

    // ---------- MINI (Dropdown) ----------
    if (variant === 'mini') {
        return (
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        aria-label="Select language"
                        className={clsx(
                            className
                        )}
                    >
                        <Globe className="w-5 text-primary-0 h-5 stroke-1" />
                    </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end" className="min-w-36">
                    {locales.map(({ key }) => (
                        <DropdownMenuItem
                            key={key}
                            onClick={() => handleLanguageChange(key)}
                            className="flex items-center justify-between"
                        >
                            <span>{dictionary.ui?.language[key]}</span>
                            <span className="text-xs text-neutral-500 font-mono">
                                {key.toUpperCase()}
                            </span>
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        );
    }

    // ---------- FULL (Sheet) ----------
    return (
        <Sheet>
            <SheetTrigger asChild>
                <button
                    aria-label="Select language"
                    className={clsx(
                        'flex items-center gap-2 w-1/2 bg-primary-100 px-3 py-2 rounded-xs text-sm tracking-wide hover:opacity-70 transition',
                        className
                    )}
                >
                    <Globe className="w-5 h-5 stroke-[1.5]" />
                    <span>{dictionary.ui?.language.selectLanguage}</span>
                </button>
            </SheetTrigger>

            <SheetContent
                side="bottom"
                className="w-full [&>button]:hidden rounded-none border-t border-neutral-200 bg-white text-black p-0 flex flex-col"
            >
                <SheetTitle className="sr-only">
                    {dictionary.ui?.language.selectLanguage}
                </SheetTitle>

                <div className="flex-1 px-6 py-4 space-y-1 text-sm">
                    {locales.map(({ key }) => (
                        <button
                            key={key}
                            onClick={() => handleLanguageChange(key)}
                            className="flex items-center justify-between w-full py-3 px-2 hover:bg-neutral-50"
                        >
                            <span>{dictionary.ui?.language[key]}</span>
                            <span className="text-neutral-500 text-xs font-mono">
                                {key.toUpperCase()}
                            </span>
                        </button>
                    ))}
                </div>
            </SheetContent>
        </Sheet>
    );
}
